// client/src/utils/offlineOcr.js
// Optional fallback OCR (client-side) using tesseract.js.
// Loaded lazily to avoid impacting normal scan performance.

const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const URL_RE = /\b((https?:\/\/)?(www\.)?[a-z0-9][a-z0-9-]{0,62}\.[a-z]{2,}(?:\.[a-z]{2,})*(\/[^\s]*)?)\b/gi;
const URL_TEST_RE = /\b((https?:\/\/)?(www\.)?[a-z0-9][a-z0-9-]{0,62}\.[a-z]{2,}(?:\.[a-z]{2,})*(\/[^\s]*)?)\b/i;

const norm = (s) => String(s || '').replace(/\s+/g, ' ').trim();

const normalizeUrl = (raw) => {
  const t = norm(raw).replace(/^[\s\-–—|•·,:;]+/, '').replace(/[\s\-–—|•·,:;]+$/, '');
  if (!t) return null;
  if (/@/.test(t)) return null;
  const withProto = /^https?:\/\//i.test(t) ? t : `https://${t}`;
  try {
    const u = new URL(withProto);
    return u.toString();
  } catch {
    return null;
  }
};

function parseBasicFieldsFromText(text) {
  const fields = {
    name: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    website: '',
    address: '',
    notes: ''
  };

  const t = norm(text);
  if (!t) return fields;

  const emails = t.match(EMAIL_RE);
  if (emails?.length) fields.email = emails[0].toLowerCase();

  // Remove emails before URL scan
  const withoutEmails = t.replace(EMAIL_RE, ' ');
  const urls = withoutEmails.match(URL_RE);
  if (urls?.length) fields.website = normalizeUrl(urls[0]) || '';

  // Simple phone candidate: longest digit run (works as a basic fallback)
  const digitRuns = t.match(/(\+?\d[\d\s().-]{6,}\d)/g);
  if (digitRuns?.length) {
    const best = digitRuns
      .map((s) => ({ s, len: s.replace(/\D/g, '').length }))
      .sort((a, b) => b.len - a.len)[0];
    fields.phone = best?.s ? norm(best.s) : '';
  }

  const lines = t.split(/\n+/).map(norm).filter(Boolean);
  // Name heuristic: first line with letters, no @, no URL-ish
  const nameLine = lines.find((l) => /[A-Za-z]/.test(l) && !/@/.test(l) && !URL_TEST_RE.test(l) && !/\d/.test(l) && l.split(' ').length <= 4);
  if (nameLine) fields.name = nameLine;

  fields.notes = t.slice(0, 1200);
  return fields;
}

export async function runOfflineOcr(blob, { lang = 'eng' } = {}) {
  const { createWorker } = await import('tesseract.js');

  const worker = await createWorker(lang);
  try {
    const { data } = await worker.recognize(blob);
    const rawText = data?.text || '';
    const fields = parseBasicFieldsFromText(rawText);
    return {
      text: rawText,
      fields
    };
  } finally {
    await worker.terminate();
  }
}

